import React from "react";
import { api } from "./api.jsx";
import { useApp } from "./store.jsx";
import { shrinkPhoto } from "./shrink-photo.js";
import { useConfirm } from "./chrome.jsx";

const { Button, IconButton, EmptyState } = window.CardboardmarketDesignSystem_2a3239;

/**
 * Adding and removing the photographs on one listing.
 *
 * A separate file because it is the first thing in this app that handles a file
 * from the person using it, and that carries rules the rest of the client does
 * not have to think about.
 *
 * The upload goes to our own API rather than straight to storage. A browser
 * uploading directly would need a credential, and any credential a browser holds
 * is a credential anybody holds. It also means the stripping happens somewhere
 * the trader cannot skip: a photo from a phone carries the coordinates of wherever
 * it was taken, and this product exists on the promise that nobody learns where
 * you live. See api/src/lib/photos.ts.
 */
/**
 * The photographs of one listing, as a SECTION rather than a screen of its own.
 *
 * It was its own dialog behind its own button, beside "Edit listing" — two
 * buttons for one intent, which is what the owner spotted looking at his own
 * Selling tab: "at this point it doesn't make sense to maintain different
 * buttons for Edit listing and Upload photos". Quite right. Everything here is
 * "change this listing", and splitting it in two made a seller choose which
 * dialog their change lived in before they could make it.
 *
 * NO DIALOG, NO FOOTER, NO CLOSE. It saves as it goes — every add and every
 * removal is its own request against a listing that already exists — so there
 * is nothing here for a Save button to do and nothing for Cancel to undo. That
 * is worth knowing at the call site: this section is not part of the form's
 * patch, and the form's Cancel does not reach it.
 */
export function PhotoFields({ listing }) {
  const { notify, loadMine, t } = useApp();
  const [photos, setPhotos] = React.useState(listing.photos || []);
  const [busy, setBusy] = React.useState(false);
  const inputRef = React.useRef(null);
  /* The camera's own input — see the note beside it. */
  const cameraRef = React.useRef(null);

  const max = 6;
  const room = Math.max(0, max - photos.length);

  const add = async (files) => {
    // One at a time, in order, so a rejected file names itself rather than
    // failing a batch and leaving the trader to guess which one was the problem.
    const chosen = Array.from(files).slice(0, room);
    if (!chosen.length) return;
    setBusy(true);
    let added = 0;
    for (const original of chosen) {
      /*
       * The SECOND upload path, and it needs the same treatment as the first.
       *
       * Adding a photo to an existing listing sends exactly what the posting
       * form sends, from exactly the same phone. Shrinking one and not the other
       * would mean the fast path depends on which screen somebody happened to
       * start from — see app/shrink-photo.js.
       */
      const file = await shrinkPhoto(original);
      try {
        const r = await api.addListingPhoto(listing.id, file);
        setPhotos((p) => p.concat([r.photo]));
        added += 1;
      } catch (e) {
        // Named, because "upload failed" tells somebody holding a photo nothing
        // about what to do differently.
        notify(
          e && e.code === 'too_large' ? t("“{name}” is too big. 12 MB is the limit.", { name: file.name })
          : e && (e.code === 'unsupported_type' || e.code === 'not_an_image')
            ? t("“{name}” is not a photo we can read.", { name: file.name })
          : e && e.code === 'uploads_unavailable' ? t("Photo uploads are switched off right now.")
          : t("“{name}” did not upload.", { name: file.name }),
        );
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    if (added) {
      notify(added === 1 ? t("Photo added.") : t("{n} photos added.", { n: added }));
      // The listing on the screen behind this dialog still has the old count.
      loadMine();
    }
  };

  /* Asked first: a removed photo is deleted from storage, and only a new one replaces it.
     Opened from inside the edit dialog, so Back closes this one alone. */
  const [confirm, confirmDialog] = useConfirm();
  const remove = async (photo) => {
    const yes = await confirm({
      title: t("Remove this photo?"),
      description: t("It is deleted, and traders see the listing without it. You can add another."),
      confirmLabel: t("Remove photo"),
    });
    if (!yes) return;
    try {
      await api.removeListingPhoto(listing.id, photo.id);
      setPhotos((p) => p.filter((x) => x.id !== photo.id));
      loadMine();
    } catch (e) {
      notify(t("That photo could not be removed."));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {confirmDialog}
      <div style={{ font: 'var(--type-label)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-label)', color: 'var(--text-muted)' }}>
        {t("Your photos")}
      </div>
      {/*
        Said once, here, because it is the sentence that makes somebody willing
        to photograph a card on their own kitchen table — and because these are
        judged instead of the catalogue picture, which is the same for every
        copy in print.
      */}
      <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        {t("Your own pictures of this copy. They save as you add them.")}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {photos.length ? (
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            {photos.map((p) => (
              <div key={p.id} style={{ position: 'relative', lineHeight: 0 }}>
                <img src={p.thumbUrl} alt="" loading="lazy"
                  style={{ width: 84, height: 108, objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-hairline)' }} />
                <div style={{ position: 'absolute', top: 2, right: 2 }}>
                  <IconButton icon="x" label={t("Remove this photo")} onClick={() => remove(p)} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon="image" title={t("No photos yet")}
            description={t("Front, back, and a close-up of anything a buyer would want to see.")} />
        )}

        {/*
          A hidden input driven by a real button.
          A bare file input cannot be styled and reads as "Choose file / no file
          chosen", which says nothing about what to choose or how many are left.
        */}
        <input
          ref={inputRef}
          type="file"
          /* `image/*` for the same reason as the camera's: the chooser offers
             the apps that MATCH, and a list of five types hides the rest — on
             one handset it showed nothing at all. The real check is the format
             read out of the bytes, server-side. */
          accept="image/*"
          multiple
          onChange={(e) => add(e.target.files)}
          style={{ display: 'none' }}
        />
        {/*
          AND A SECOND ONE THAT ASKS FOR THE CAMERA.
          Reported from a phone: "when in my smartphone try to post a card and
          click the Add a photo button, it only lets me select an image from my
          device, but doesnt give me the option to take a photo now".

          Nothing was restricting us — the button simply never asked. `capture`
          is the attribute that tells a phone to open its camera; a laptop
          ignores it and shows the file picker, which is the right answer on a
          laptop. The scanner has used exactly this as its own fallback since it
          was written.

          `image/*` RATHER THAN THE LIST BESIDE IT, and that is the other half of
          the bug: Android offers a chooser the apps whose MIME types MATCH, and
          a camera does not advertise `image/heif`. A camera produces a JPEG,
          which the strict list above still guards for anything chosen from
          storage.

          ONE AT A TIME, no `multiple`: a camera returns one photograph, and
          asking for several only makes the control lie about what it does.
        */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => add(e.target.files)}
          style={{ display: 'none' }}
        />
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="outline" iconLeft="camera" disabled={busy || !room}
            onClick={() => cameraRef.current && cameraRef.current.click()}>
            {t("Take a photo")}
          </Button>
          <Button variant="outline" iconLeft="upload" disabled={busy || !room}
            onClick={() => inputRef.current && inputRef.current.click()}>
            {busy ? t("Uploading…") : t("Add a photo")}
          </Button>
          <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            {room ? t("{n} of {max} left", { n: room, max }) : t("That is the most a listing can carry.")}
          </span>
        </div>

        {/*
          Said plainly, because it is the reassurance somebody actually needs
          before photographing something on their own kitchen table.
        */}
        <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {t("Photos are stripped of their hidden data — including the location your phone records — before anyone else can see them.")}
        </span>
      </div>
    </div>
  );
}

